(ns ycweb.app.pos.payment
  (:require
   [rum.core :as rum]
   [ycweb.components.forms :as forms]
   [ycweb.components.buttons :as buttons]
   [ycweb.app.pos.integration.payment :as payment]
   [ycweb.app.pos.integration.mpos :as mpos]
   [ycweb.util.core :as u]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.grid :as grid]
   [util.string :as str]
   [ycweb.app.pos.sell :as sell]
   [ycweb.components.headings :as headings]
   [ycweb.components.feedback :as feedback]
   [cemerick.url :as url]
   [ycweb.components.common :as cmn]
   [ycweb.components.flow :as flow]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.hash :as hash]
   [clojure.set :as set]
   [ycweb.components.versioncheck :as versioncheck]
   [ycweb.components.header :as header]
   [ycweb.app.pos.menu :as menu]))

(defn capture-payment [app-atom pmttype pmt]
  (grid/dispatch-action app-atom :payment/capture (assoc pmt :pmttype pmttype)))

(defn provider []
  ;; Check the device capability live — a cached :exists? snapshot goes stale
  ;; when the app-atom is reset while the JS bridge is unavailable (e.g. after sleep).
  (when (mpos/on-terminal?)
    "Thumbzup"))

(def ecentric-pmttypes
  "pmttypes that drive the Ecentric card terminal. A void of one of these stays visible on
   the payment screen as Voided."
  #{"Thumbzup" "Payflex"})

(defn menu-modal [app-atom]
  (grid/menu-modal app-atom
    {:items
     [{:label "Manual Barcode"
       :action :sell/open-barcode-modal}
      {:label "Manual Voucher"
       :action :sell/open-voucher-modal}
      {:label "Open Till"
       :action :sell/open-till-modal}
      #_{:label "Online Sale"
         :action :sell/load-online-order}
      {:label "Discard Sale"
       :action :sell/open-discard-confirm-modal
       :style
       {:full true :large true :negative true
        :icon-left :delete :text-default true :icon-class :3-2}}
      {:label "Cancel"
       :action :sell/cancel-cart-menu-modal
       :style
       {:full true :large true :text-link true :on-dark true}}]}))

(rum/defc screen-wrapper < rum/reactive
  [app-atom progress-step body foot-note call-to-action]

  (let [sell-atom (rum/cursor app-atom :sell)
        menu-atom (rum/cursor app-atom :menu)]
    [:div.page-body
     (header/header app-atom "Sell" {})
     (header/menu-overlay menu-atom)
     (header/menu app-atom (menu/select-menus app-atom))

     (into
       [:div.page-body__main-content
        [:nav.section.u-text-right
         (buttons/button
           "Refresh Payments" #(grid/dispatch-action app-atom :sell/refresh-cart {})
           {:icon-right :refresh :raised true :text-default true :secondary true})]

        [:div.section
         (forms/progress-steps
           (forms/progress-step
             (if (cmn/curact sell-atom [:cart :resumedcart])
               sell/resumed-sell-steps
               sell/sell-steps)
             progress-step))]]

       body)

     (into
       [:aside.page-body__footer]
       foot-note)
     call-to-action]))

(defn slat-icon
  "Payment slat logo — a background-image sprite keyed by icon--<name> class."
  [icon icon-size]
  [:i {:class (u/bem :icon [:block icon icon-size])}])

(rum/defc payment-option-slat
  [title subtitle amount icon icon-size
   right-icon right-icon-size click-fn & {:keys [disabled]}]
  [:a.payment-option-slat
   {:class (u/bem :slat [(when disabled :is-disabled)])
    :on-click (when-not disabled click-fn)}
   [:div.payment-option-slat__logo-wrapper
    (slat-icon icon icon-size)]
   [:div.payment-option-slat__title
    title " "
    (when subtitle
      [:span.payment-option-slat__subtitle subtitle])]
   (when (and amount (not (zero? amount)))
     [:div.payment-option-slat__amount
      (str "R" (u/currency-format amount))])
   [:div.payment-option-slat__icon-wrapper
    [:i {:class (u/bem :icon [:block right-icon right-icon-size])}]]])

(rum/defc payment-line
  [title subtitle amount icon icon-size
   right-icon right-icon-size slat-status click-fn]
  [:div.payment-option-slat
   {:class (u/bem :slat [(when slat-status slat-status)])}
   [:div.payment-option-slat__logo-wrapper
    (slat-icon icon icon-size)]
   [:div.payment-option-slat__title title]
   [:div.payment-option-slat__amount (str "R" (u/currency-format amount))]
   (when click-fn
     [:a.payment-option-slat__icon-wrapper
      {:on-click click-fn}
      [:i
       {:class (u/bem :icon [right-icon :block])}
       [:span.u-hidden-from-view "Options"]]])])

(def type-lookup
  {"Cash"
   {:name "Cash" :icon :cash :icon-size :3-3 :style :basic
    :confirm-msg "Cash Payment Confirmed"}

   "Card"
   {:name "Card (Manual)" :icon :credit-card :icon-size :2-1 :style :basic
    :confirm-msg "Card Payment Confirmed"}

   "Thumbzup"
   {:name "Card" :icon :credit-card :icon-size :2-1 :style :basic
    :confirm-msg "Card Payment Confirmed"}

   "Payflex"
   {:name "Payflex" :icon :payflex :icon-size nil :style :basic
    :confirm-msg "Payflex Payment Confirmed"}

   "Yoco"
   {:name "Card (Yoco)" :icon :credit-card :icon-size :2-1 :style :basic
    :confirm-msg "Card Payment Confirmed"}

   "Voucher"
   {:name "Voucher" :icon :gift-card :icon-size :3-3 :style :basic
    :confirm-msg "Voucher Redeemed"}

   "Zapper"
   {:name "Zapper" :icon :zapper :icon-size nil
    :logo "https://res.cloudinary.com/yuppiechef/image/upload/v1503397904/pos-ui/zapper-logo.svg"
    :style :card}

   "Snapscan"
   {:name "SnapScan" :icon :snapscan :icon-size nil
    :logo "https://res.cloudinary.com/yuppiechef/image/upload/v1503397903/pos-ui/snapscan-logo.svg"
    :style :card}

   "Staff"
   {:name "Staff" :icon :user :icon-size nil :style :basic
    :confirm-msg "Staff Discount Confirmed"}

   "Trade"
   {:name "Trade" :icon :user :icon-size nil :style :basic
    :confirm-msg "Trade Discount Applied"}

   "eBucks"
   {:name "eBucks" :icon :ebucks :icon-size :2-1 :style :basic
    :confirm-msg "eBucks Payment Confirmed"}})

(defn confirm-check-modal [app-atom typedef confirm-pmt]
  (grid/confirm-modal app-atom
    {:payment confirm-pmt
     :typedef typedef
     :title (str "Check " (:name typedef) " payment?")
     :text
     [[:p.paragraph
       "Do you want to check the status of this " (:name typedef) " payment for R"
       (u/currency-format (:amount confirm-pmt)) "?"]]
     :confirm-key :payment/check-payment-status
     :confirm-text "Check"
     :cancel-key :payment/check-payment-status-cancel}))

(rum/defc pending-payment-list < rum/reactive [app-atom totals-atom]
  (let [payments (cmn/curact totals-atom :pending-payment-list)]
    (when-not (empty? payments)
      (into
        [:nav.payment-options-list.slats
         [:h3.slat {:class (u/bem :heading-slat [:alt])}
          [:span.heading-slat__icon-wrapper
           [:i {:class (u/bem :icon [:block :warning :3-3])}]]
          [:span.heading-slat__text
           "Pending payments:"]]]
        (map
          (fn [p]
            (let [typedef (get type-lookup (:pmttype p))]
              (payment-line (:name typedef) "" (- (:amount p) (or (:change p) 0)) (:icon typedef) (:icon-size typedef)
                :dots-menu nil :warning
                #(confirm-check-modal app-atom typedef p))))
          payments)))))

(rum/defc voided-payment-list < rum/reactive [app-atom totals-atom]
  (let [payments (cmn/curact totals-atom :voided-payment-list)]
    (when-not (empty? payments)
      (into
        [:nav.payment-options-list.slats
         [:h3.slat {:class (u/bem :heading-slat [:alt])}
          [:span.heading-slat__icon-wrapper
           [:i {:class (u/bem :icon [:block :error :3-3])}]]
          [:span.heading-slat__text
           "Voided payments:"]]]
        (map
          (fn [p]
            (let [typedef (get type-lookup (:pmttype p))]
              (payment-line (:name typedef) "" (- (:amount p) (or (:change p) 0)) (:icon typedef) (:icon-size typedef)
                :dots-menu nil :error
                #(confirm-check-modal app-atom typedef p))))
          payments)))))

(defn confirm-remove-modal [app-atom typedef confirm-pmt]
  (grid/confirm-modal app-atom
    {:payment confirm-pmt
     :typedef typedef
     :title
     (str "Void " (:name typedef) " payment?")
     :text
     [[:p.paragraph
       "Are you sure you want to void the " (:name typedef) " payment for R"
       (u/currency-format (:amount confirm-pmt)) "?"]
      (cond
        (= "Voucher" (:pmttype confirm-pmt)) nil

        (= "Thumbzup" (:pmttype confirm-pmt))
        [:p.u-text-status-important {:class (u/bem :paragraph [:flush])}
         [:b "Note:"]
         " The payment will be refunded through the pos terminal after the customer's card is presented."]

        :else
        [:p.u-text-status-important {:class (u/bem :paragraph [:flush])}
         [:b "Note:"]
         " You will need to refund the payment manually. Voiding does not do auto-refunds."])]
     :confirm-key :payment/remove-payment
     :confirm-text "Void"
     :cancel-key :payment/remove-payment-cancel}))

(rum/defc payment-list < rum/reactive [app-atom totals-atom]
  (let [payments (cmn/curact totals-atom :calc-payments)]
    (when-not (empty? payments)
      (into
        [:nav.payment-options-list.slats
         [:h3.slat {:class (u/bem :heading-slat [:alt])}
          [:span.heading-slat__icon-wrapper
           [:i {:class (u/bem :icon [:block :success :3-3])}]]
          [:span.heading-slat__text
           "Received payments:"]]]
        (map
          (fn [p]
            (let [typedef (get type-lookup (:pmttype p))]
              (payment-line (:name typedef) "" (- (:amount p) (or (:change p) 0)) (:icon typedef) (:icon-size typedef)
                :dots-menu nil :success
                (when-not (= "Payflex" (:pmttype p))
                  #(confirm-remove-modal app-atom typedef p)))))
          payments)))))

(rum/defc payment-buttons < rum/reactive [setup totals-atom finish-fn]
  (let [owing (cmn/curact totals-atom :owing)]
    (grid/pos-call-to-action
      (assoc setup
        :right-text
        (if (pos? owing)
          (str "Owing: R" (u/currency-format owing))
          "Paid")
        :right-fn (when-not (pos? owing) finish-fn)
        :right-plain true))))

(defn copy-amount-cents [s tp]
  (assoc-in s [:payments tp :amount]
    (get-in s [:sell :totals-atom :owing])))

(defn copy-amount [s tp]
  (->
    (copy-amount-cents s tp)
    (update-in [:payments tp :amount] #(u/currency-format % 2 ""))))

(defmethod grid/action :payment/cash [app-atom config]
  (t/transition-next app-atom :payment/cash))

(defn prep-card-payment
  "Seed the shared [:payments :card] slot for the card screen. `launchtype` is :sale or
   :qr-sale, read back in the :pos/payment-pending handler."
  [s launchtype]
  (->
    s
    (copy-amount :card)
    (assoc-in [:payments :card :amount]
      (u/currency-format (:owing (:totals-atom (:sell s))) 2 ""))
    (assoc-in [:payments :card :receipt-cell]
      (get-in s [:sell :delivery :cell]))
    (assoc-in [:payments :card :receipt-email]
      (get-in s [:sell :delivery :email]))
    (assoc-in [:payments :card :launchtype] launchtype)))

(defmethod grid/action :payment/card [app-atom config]
  (swap! app-atom prep-card-payment :sale)
  (t/transition-next app-atom :payment/card))

(defmethod grid/action :payment/payflex [app-atom config]
  (swap! app-atom prep-card-payment :qr-sale)
  (t/transition-next app-atom :payment/card))

(defmethod grid/action :payment/voucher [app-atom config]
  (swap! app-atom #(copy-amount % :voucher))
  (t/transition-next app-atom :payment/voucher))

(defn generate-zapper-reference []
  ; Max of 30 characters for Zapper reference
  (let [uuid
        (->
          (u/rand-uuid)
          (str)
          (str/replace #"-" ""))]
    (subs uuid 0 (min (count uuid) 30))))

(defmethod grid/action :payment/zapper [app-atom config]
  (swap! app-atom
    (fn [s]
      (->
        s
        (copy-amount :zapper)
        (update-in [:payments :zapper :reference] #(or % (generate-zapper-reference))))))
  (t/transition-next app-atom :payment/zapper))

(defmethod grid/action :payment/snapscan [app-atom config]
  (swap! app-atom
    (fn [s]
      (->
        s
        (copy-amount-cents :snapscan)
        (update-in [:payments :snapscan :reference] #(or % (u/rand-uuid))))))
  (t/transition-next app-atom :payment/snapscan))

(defmethod grid/action :payment/ebucks [app-atom config]
  (swap! app-atom #(copy-amount % :ebucks))
  (t/transition-next app-atom :payment/ebucks))

(defmethod grid/action :payment/approve-discount [app-atom config]
  (toaster/loading-overlay app-atom "Checking Credentials...")
  (comms/send :pos/approve-discount config))

(defmethod comms/receive :pos/approve-discount [app-atom config]
  (when (:success config)
    (let [return-to (get-in @app-atom [:payments :approve-discount-return] :payment/staff)]
      (toaster/open-toast app-atom :success "Discount Approved" {})
      (swap! app-atom
        (fn [state]
          (->
            state
            (assoc-in [:payments :approved-discount?] config)
            (update :payments dissoc :approve-discount-return))))
      (when (get-in @app-atom [:sell :delivery :lookup :trade-user?])
        (comms/send :pos/payment
          (sell/sell-comms app-atom {:pmttype "Trade"})))
      (t/transition-next app-atom return-to))))

(rum/defc approve-discount-screen < rum/reactive [app-atom]
  (screen-wrapper app-atom :payment
    [[:div.section.u-margin-top-24
      [:h2.u-text-center
       {:class (u/bem :heading [:standard])}
       "Scan a Supervisor's Tag"]
      [:div
       [:i.u-block-center
        {:class
         (u/bem
           :icon
           [:2-1 :barcode-scan :light-force :enormous :block])}]]]]
    []
    (grid/pos-call-to-action
      {:single-style :secondary-noborder
       :single-icon-left :arrow-left
       :single-text "Back"
       :single-fn #(t/transition-prev app-atom)})))

(defmethod flow/render-screen :payment/approve-discount [app-atom screen]
  (approve-discount-screen app-atom))

(defmethod flow/barcode-dispatch-screen :payment/approve-discount [_ app-atom scan]
  (let [approval-atom (rum/cursor-in app-atom [:payments])]
    (swap! app-atom assoc-in [:payments :manager-tag] scan)
    (grid/dispatch-action app-atom :payment/approve-discount @approval-atom)))

(defmethod grid/action :payment/staff [app-atom config]
  (swap! app-atom
    (fn [s]
      (->
        s
        (assoc-in [:payments :staff :reference]
          (get-in s [:sell :delivery :email])))))
  (t/transition-next app-atom :payment/approve-discount))

(rum/defc payment-screen < rum/reactive [app-atom]
  (let [totals-atom (sell/totals app-atom)
        voucher-payments (reduce #(+ %1 (if (= "Voucher" (:pmttype %2)) 1 0)) 0 (:calc-payments (rum/react totals-atom)))]
    (screen-wrapper app-atom :payment
      [[:div
        (pending-payment-list app-atom totals-atom)
        (payment-list app-atom totals-atom)
        (voided-payment-list app-atom totals-atom)

        (when (and (empty? (cmn/curact totals-atom :pending-payment-list)) (pos? (cmn/curact totals-atom :owing)))
          [:nav.payment-options-list.slats
           [:h3.slat {:class (u/bem :heading-slat [:alt])}
            [:span.heading-slat__icon-wrapper
             [:i {:class (u/bem :icon [:block :payment-options :2-1])}]]
            [:span.heading-slat__text
             "Payment options:"]]

           (payment-option-slat "Cash" "" nil :cash :3-3 :arrow-right :2-1
             #(grid/dispatch-action app-atom :payment/cash {}))

           (payment-option-slat "Card" "(Visa, Master, Diners or Amex)" nil :credit-card :2-1 :arrow-right :2-1
             #(grid/dispatch-action app-atom :payment/card {}))

           (payment-option-slat "Voucher" "" nil :gift-card :3-3 :arrow-right :2-1
             #(grid/dispatch-action app-atom :payment/voucher {}))

           (let [owing (cmn/curact totals-atom :owing)
                 payflex-allowed? (<= 1000 owing 5000000)]
             (payment-option-slat "Payflex"
               (cond
                 (< owing 1000) "(R10 minimum)"
                 (> owing 5000000) "(R50 000 maximum)"
                 :else "(Requires App)")
               nil :payflex nil :arrow-right :2-1
               #(grid/dispatch-action app-atom :payment/payflex {})
               :disabled (not payflex-allowed?)))

           (payment-option-slat "Zapper" "(Requires App)" nil :zapper nil :arrow-right :2-1
             #(grid/dispatch-action app-atom :payment/zapper {}))

           (payment-option-slat "SnapScan" "(Requires App)" nil :snapscan nil :arrow-right :2-1
             #(grid/dispatch-action app-atom :payment/snapscan {}))

           (payment-option-slat "eBucks" "" nil :ebucks nil :arrow-right :2-1
             #(grid/dispatch-action app-atom :payment/ebucks {}))

           (when
             (and
               (cmn/curact app-atom [:sell :delivery :staff])
               (empty? (filter #(= "Staff" (:pmttype %)) (cmn/curact app-atom [:sell :cart :payments]))))
             (payment-option-slat "Staff" "" nil :user nil :arrow-right :2-1
               #(grid/dispatch-action app-atom :payment/staff {})))])

        (sell/order-summary totals-atom)]]
      []
      (payment-buttons
        {:left-fn
         (if (> voucher-payments 0)
           #(toaster/open-toast app-atom :error "Remove voucher to edit cart" {})
           #(t/transition-prev app-atom :sell))}
        totals-atom
        #(grid/dispatch-action app-atom :sell/wrapup-cart {})))))

(defmethod flow/render-screen :payment [app-atom screen]
  (payment-screen app-atom))

(rum/defc cash-screen < rum/reactive
  [app-atom]
  (let [cash-atom (rum/cursor-in app-atom [:payments :cash])
        totals-atom (sell/totals app-atom)
        {:keys [owing]} (rum/react totals-atom)]
    (screen-wrapper app-atom :payment
      [[:div
        [:div.u-margin-top-24
         [:div {:class (u/bem :section [:medium])}
          (forms/input "Amount Paid in Cash:" ""
            (rum/cursor cash-atom :amount)
            {:type :currency
             :hint (str "Owing: R" (u/currency-format owing))})]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-text "Scan Till"
         :right-plain true}))))

(defmethod flow/render-screen :payment/cash [app-atom screen]
  (cash-screen app-atom))

(defmethod flow/barcode-dispatch-screen :payment/cash [_ app-atom scan]
  (println "Scanning cash till: " scan)
  (swap! app-atom assoc-in [:payments :cash :scan] scan)
  (capture-payment app-atom "Cash"
    (get-in @app-atom [:payments :cash])))

;; ========= Manual Card Modal ===========================
(rum/defc card-manual-modal-body [app-atom config-atom]
  [:div.section
   [:h2.heading.heading--standard "Capture Manual Card Payment"]
   (forms/input "Amount Paid:" "" (rum/cursor-in config-atom [:amount])
     {:type :currency})
   (forms/input "Last 4 Digits of Card:" ""
     (rum/cursor-in config-atom [:reference])
     {:type :text-number :max-length 4})])

(rum/defc card-manual-modal-footer [app-atom]
  (grid/button-row
    {:single-text "Capture Payment"
     :single-style :primary
     :single-fn
     #(grid/dispatch-modal-action app-atom :payment/capture)}))

(defmethod grid/modal-config :payment/card-manual-modal [_ _ _]
  {:body card-manual-modal-body
   :footer card-manual-modal-footer})

(defn card-manual-modal [app-atom owing]
  (grid/open-modal app-atom :payment/card-manual-modal {:amount (u/currency-format owing 2 "") :pmttype "Card"}))

(defn payflex?
  "True when the card screen is in Payflex QR mode."
  [card-atom]
  (= :qr-sale (:launchtype @card-atom)))

(rum/defc payflex-banner < rum/reactive [card-atom]
  (when (payflex? card-atom)
    [:div.section.u-margin-top-24
     (feedback/feedback :info "Payflex payment"
       "Launching the terminal will display a Payflex QR code for the customer to scan.")]))

(rum/defc provider-card-screen < rum/reactive
  [app-atom]
  (let [totals-atom (sell/totals app-atom)
        card-atom (rum/cursor-in app-atom [:payments :card])
        {:keys [owing]} (rum/react totals-atom)]
    (screen-wrapper app-atom :payment
      [[:div
        (payflex-banner card-atom)
        [:form.u-margin-top-24
         [:div {:class (u/bem :section [:medium])}

          (forms/input "Amount Paid Using Card:" ""
            (rum/cursor card-atom :amount)
            {:type :currency
             :hint (str "Owing: R" (u/currency-format owing))})]

         (when-not (payflex? card-atom)
           [:div.section.u-text-right
            (buttons/button "Manual Capture"
              #(card-manual-modal app-atom owing)
              {:secondary true :icon-left :pencil})

            [:div.u-margin-top-16
             [:p.u-text-12 {:class (u/bem :paragraph [:small])}
              "Use Manual for Amex and Diners."]]])]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-fn
         ;; Resolve the provider at click time, not render time — the capability can change
         ;; between the two (device sleep/wake), and a stale nil here would send a pending
         ;; payment with no pmttype.
         #(grid/dispatch-action app-atom :payment/start-card-payment
            {:payment {:amount (:amount @card-atom)
                       :pmttype (if (payflex? card-atom) "Payflex" (provider))}})}))))

(rum/defc manual-card-screen < rum/reactive
  [app-atom]
  (let [totals-atom (sell/totals app-atom)
        card-atom (rum/cursor-in app-atom [:payments :card])
        {:keys [owing]} (rum/react totals-atom)]
    (screen-wrapper app-atom :payment
      [[:div
        [:form.u-margin-top-24
         [:div {:class (u/bem :section [:medium])}
          (forms/input "Amount Paid Using Card:" ""
            (rum/cursor card-atom :amount)
            {:type :currency
             :hint (str "Owing: R" (u/currency-format owing))})
          (forms/input "Last 4 Digits of Card:" ""
            (rum/cursor card-atom :reference)
            {:type :text-number :max-length 4})]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-fn
         #(grid/dispatch-action app-atom :payment/capture (assoc @card-atom :pmttype "Card"))}))))

;; ======== Thumbzup Emulation =======================
(defmethod grid/action :thumbzup-emulation/success [app-atom config _]
  (swap! app-atom update-in [:payments :card] assoc :provider "Thumbzup" :emulate :success))

(defmethod grid/action :thumbzup-emulation/cancelled [app-atom config _]
  (swap! app-atom update-in [:payments :card] assoc :provider "Thumbzup" :emulate :cancelled))

(defmethod grid/action :thumbzup-emulation/timeout [app-atom config _]
  (swap! app-atom update-in [:payments :card] assoc :provider "Thumbzup" :emulate :timeout))

(defmethod grid/action :thumbzup-emulation/crash [app-atom config _]
  (swap! app-atom update-in [:payments :card] assoc :provider "Thumbzup" :emulate :crash))

(defn card-emulation-menu [app-atom config]
  (grid/menu-modal app-atom
    {:items
     [{:label "Successful"
       :action :thumbzup-emulation/success}

      {:label "Cancelled"
       :action :thumbzup-emulation/cancelled}

      {:label "Timeout"
       :action :thumbzup-emulation/timeout}

      {:label "Crash"
       :action :thumbzup-emulation/crash
       :style
       {:full true :large true :negative true
        :icon-left :warning :text-default true :icon-class :3-2}}

      {:label "Cancel"
       :action :thumbzup-emulation/menu-cancel
       :style
       {:full true :large true :text-link true :on-dark true}}]}))

;; ======== Yoco Emulation =======================
(defmethod grid/action :yoco-emulation/success [app-atom config _]
  (swap! app-atom update-in [:payments :card] assoc :provider "Yoco" :emulate :success))

(defmethod grid/action :yoco-emulation/cancelled [app-atom config _]
  (swap! app-atom update-in [:payments :card] assoc :provider "Yoco" :emulate :cancelled))

(defmethod grid/action :yoco-emulation/timeout [app-atom config _]
  (swap! app-atom update-in [:payments :card] assoc :provider "Yoco" :emulate :timeout))

(defmethod grid/action :yoco-emulation/crash [app-atom config _]
  (swap! app-atom update-in [:payments :card] assoc :provider "Yoco" :emulate :crash))

(defn yoco-emulation-menu [app-atom config]
  (grid/menu-modal app-atom
    {:items
     [{:label "Successful"
       :action :yoco-emulation/success}

      {:label "Cancelled"
       :action :yoco-emulation/cancelled}

      {:label "Timeout"
       :action :yoco-emulation/timeout}

      {:label "Crash"
       :action :yoco-emulation/crash
       :style
       {:full true :large true :negative true
        :icon-left :warning :text-default true :icon-class :3-2}}

      {:label "Cancel"
       :action :yoco-emulation/menu-cancel
       :style
       {:full true :large true :text-link true :on-dark true}}]}))

(rum/defc dev-card-screen < rum/reactive
  [app-atom]
  (let [totals-atom (sell/totals app-atom)
        card-atom (rum/cursor-in app-atom [:payments :card])
        {:keys [provider emulate]} (rum/react card-atom)
        {:keys [owing]} (rum/react totals-atom)]
    (screen-wrapper app-atom :payment
      [[:div
        (payflex-banner card-atom)
        [:form.u-margin-top-24
         [:div {:class (u/bem :section [:medium])}

          (forms/input "Amount Paid Using Card:" ""
            (rum/cursor card-atom :amount)
            {:type :currency
             :hint (str "Owing: R" (u/currency-format owing))})]

         (when-not (payflex? card-atom)
           [:div.section.u-text-right
            (buttons/button "Manual Capture"
              #(card-manual-modal app-atom owing)
              {:secondary true :icon-left :pencil})

            [:div.u-margin-top-16
             [:p.u-text-12 {:class (u/bem :paragraph [:small])}
              "Use Manual for Amex and Diners."]]])

         [:div.section.u-text-right
          (buttons/button "Card Payment"
            #(card-emulation-menu app-atom {:amount owing})
            {:secondary true :icon-left :credit-card})

          [:div.u-margin-top-16
           [:p.u-text-12 {:class (u/bem :paragraph [:small])}
            "Emulate card payment"]]]]]]

      [(when provider
         [:p.paragraph.paragraph--flush.u-text-center
          provider " will emulate '" (name emulate) "'"])]
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-text "Next"
         :right-fn
         (when provider
           #(grid/dispatch-action app-atom :payment/start-card-payment
              {:payment {:amount (:amount @card-atom)
                         :pmttype (if (payflex? card-atom) "Payflex" provider)
                         :emulate emulate}}))}))))

(defn card-screen [app-atom]
  (cond
    (versioncheck/dev-enabled app-atom)
    (dev-card-screen app-atom)

    (provider)
    (provider-card-screen app-atom)

    :else
    (manual-card-screen app-atom)))

(defmethod flow/render-screen :payment/card [app-atom screen]
  (card-screen app-atom))

(rum/defc voucher-screen < rum/reactive
  [app-atom]

  (let [totals-atom (sell/totals app-atom)
        voucher-atom (rum/cursor-in app-atom [:payments :voucher])
        {:keys [owing total]} (rum/react totals-atom)
        voucher (rum/react voucher-atom)]

    (screen-wrapper app-atom :payment
      [[:div
        [:form.u-margin-top-24
         [:div {:class (u/bem :section [:medium])}
          (forms/input "Scan or Enter Voucher Number:" ""
            (rum/cursor voucher-atom :code)
            {:type :barcode
             :change-fn #(grid/dispatch-action app-atom :payment/voucher-lookup @voucher-atom)})
          (when (:valid voucher)
            [:div.u-margin-top-24
             (feedback/feedback :success
               (cond
                 (:amount voucher)
                 (if (> (:amount-available voucher) (:amount voucher))
                   [:span
                    [:b (str "Redeem R" (str (u/currency-format (:amount voucher))))]
                    (str " (of R" (str (u/currency-format (:amount-available voucher))) ")")]
                   (str "Redeem R" (str (u/currency-format (:amount voucher)))))

                 (:percentage voucher)
                 (str "Voucher value: " (:percentage voucher) "% (R"
                   (u/currency-format (* total (/ (:percentage voucher) 100))) ")"))

               (if (and (:amount voucher)
                     (> (:amount-available voucher) (:amount voucher)))
                 (str "You will have R" (str (u/currency-format (- (:amount-available voucher) (:amount voucher)))) " left on this voucher.")
                 ""))])

          (when (and voucher (not (:valid voucher)) (:message voucher))
            [:div.u-margin-top-24
             (feedback/feedback :error "Voucher code invalid"
               (:message voucher))])]]]]
      []
      (grid/pos-call-to-action
        {:left-fn
         #(do
            (swap! app-atom assoc-in [:payments :voucher] {})
            (t/transition-prev app-atom))
         :right-text "Redeem"
         :right-fn
         (if (:valid voucher)
           #(capture-payment app-atom "Voucher" (update @voucher-atom :amount (fn [i] (/ i 100))))
           nil)}))))

(defmethod flow/render-screen :payment/voucher [app-atom screen]
  (voucher-screen app-atom))

(defmethod flow/barcode-dispatch-screen :payment/voucher [_ app-atom scan]
  (toaster/open-subtle-progress app-atom)
  (let [delim "/vch/"
        lowercase-scan (str/lower-case scan)
        subs-idx
        (and
          (str/includes? lowercase-scan delim)
          (+ (count delim) (str/index-of lowercase-scan delim)))

        voucher-code
        (when subs-idx (first (str/split (subs scan subs-idx) #"/")))]
    (swap! app-atom assoc-in [:payments :voucher :code] (or voucher-code scan))
    (grid/dispatch-action app-atom :payment/voucher-lookup {:code (or voucher-code scan)})))

(defmethod grid/action :payment/voucher-lookup [app-atom voucher]
  (let [state @app-atom
        totals
        (sell/calc-totals-app-state state)]
    (toaster/open-subtle-progress app-atom)
    (swap! app-atom assoc-in [:payments :voucher :message] nil)
    (comms/send :pos/voucher-lookup
      (sell/sell-comms app-atom
        {:code (:code voucher) :carttotal (:subtotal totals)}))))

(defmethod comms/receive :pos/voucher-lookup [app-atom e]
  (let [state @app-atom
        totals (sell/calc-totals-app-state state)
        amount (min (:owing totals) (:amount e))]
    (toaster/close-subtle-progress app-atom)
    (when (= (:code e) (get-in state [:payments :voucher :code]))
      (swap! app-atom assoc-in [:payments :voucher]
        {:reference (:code e) :amount amount #_:percentage #_(:percentage e)
         :amount-available (:amount e)
         :valid true}))))

(defmethod comms/receive :pos/voucher-error [app-atom e]
  (toaster/close-subtle-progress app-atom)
  (when (= (:code e) (get-in @app-atom [:payments :voucher :code]))
    (swap! app-atom assoc-in [:payments :voucher]
      {:valid false :message (:message e) :code (:code e)})))

(defn build-inventory-item [[vuuid store-items]]
  (let [qty (count store-items)]
    {:Name (:title (first store-items))
     :Quanitity qty
     :Total (* 100 (reduce + (map :price store-items)))
     :ProductCode vuuid}))

;; ========= Zapper Manual Modal ===========================
(rum/defc zapper-manual-modal-body < rum/reactive [app-atom config-atom]
  [:div.section
   [:h2.heading.heading--standard "Capture Zapper Payment"]
   (forms/input
     "Payment Reference:" ""
     (rum/cursor-in config-atom [:reference])
     {:auto-focus (cmn/auto-focus? app-atom)
      :hint "The customer will find this in their payment history"})
   (forms/input "Amount Paid:" "" (rum/cursor-in config-atom [:amount])
     {:type :currency})])

(rum/defc zapper-manual-modal-footer [app-atom]
  (grid/button-row
    {:single-text "Capture Payment"
     :single-style :primary
     :single-fn
     #(grid/dispatch-modal-action app-atom :payment/capture)}))

(defmethod grid/modal-config :payment/zapper-manual-modal [_ _ _]
  {:body zapper-manual-modal-body
   :footer zapper-manual-modal-footer})

(defn zapper-manual-modal [app-atom]
  (grid/open-modal app-atom :payment/zapper-manual-modal {:pmttype "ZapperManual"}))

(defn zappercode [{:keys [amount reference]} {:keys [siteid merchantid]} inventory-items]
  (str
    "https://zapapi.zapzap.mobi/zapperpointofsale/api/v2/merchants/" merchantid "/sites/" siteid
    "/qrcode/invoice"
    "?billAmount=" amount
    "&reference=" reference
    "&size=" 200
    "&currency=ZAR"
    "&inventoryitems=" (u/json-string inventory-items)))

(rum/defc zapper-screen < rum/reactive
  [app-atom]
  (let [zapper-config (cmn/curact app-atom [:config :zapper])
        cart-atom (rum/cursor-in app-atom [:sell :cart])

        totals-atom (sell/totals app-atom)
        {:keys [owing]} (rum/react totals-atom)
        inventory-items (map build-inventory-item (group-by :vuuid (:items (rum/react cart-atom))))
        pmt (cmn/curact app-atom [:payments :zapper])]
    (screen-wrapper app-atom :payment
      [[:div.u-margin-top-24
        [:div {:class (u/bem :section [:medium])}
         [:div.card.payment-card
          [:div.payment-card__logo-wrapper.u-text-center
           [:img.payment-card__logo
            {:src "https://res.cloudinary.com/yuppiechef/image/upload/v1503397904/pos-ui/zapper-logo.svg"
             :alt "Zapper"}]]
          [:div.payment-card__graphic-wrapper
           [:img {:src (zappercode (select-keys pmt [:amount :reference]) zapper-config inventory-items)}]]
          [:p.paragraph.paragraph--flush.u-text-14.u-text-uppercase.u-text-center
           "Scan QR Code to Pay"]]]
        [:div.section.u-text-right
         (buttons/button "Manual Capture"
           #(zapper-manual-modal app-atom)
           {:secondary true :icon-left :pencil})]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-fn #(grid/dispatch-action app-atom :payment/capture (assoc pmt :pmttype "Zapper"))
         :right-text "Confirm"}))))

(defmethod flow/render-screen :payment/zapper [app-atom screen]
  (zapper-screen app-atom))

;; ========= SnapScan Manual Modal ===========================

(rum/defc snapscan-manual-modal-body < rum/reactive [app-atom config-atom]
  [:div.section
   [:h2.heading.heading--standard "Capture SnapScan Payment"]
   (forms/input "SnapScan Authcode / ID:" "" (rum/cursor-in config-atom [:reference])
     {:auto-focus (cmn/auto-focus? app-atom) :type :string})
   (forms/input "Amount Paid:" "" (rum/cursor-in config-atom [:amount])
     {:type :currency})])

(rum/defc snapscan-manual-modal-footer [app-atom]
  (grid/button-row
    {:single-text "Capture Payment"
     :single-style :primary
     :single-fn
     #(grid/dispatch-modal-action app-atom :payment/capture)}))

(defmethod grid/modal-config :payment/snapscan-manual-modal [_ _ _]
  {:body snapscan-manual-modal-body
   :footer snapscan-manual-modal-footer})

(defn snapscan-manual-modal [app-atom]
  (grid/open-modal app-atom :payment/snapscan-manual-modal {:pmttype "SnapscanManual"}))

(defn snapscancode [{:keys [amount reference]} snapscan-endpoint]
  (str
    "https://www.yuppiechef.com/api/qr?size=200&code="
    "https%3A%2F%2Fpos.snapscan.io%2Fqr%2F"
    snapscan-endpoint "%3Famount%3D" amount "%26id%3D"
    (url/url-encode reference)))

(rum/defc snapscan-screen < rum/reactive
  [app-atom]
  (let [snapscan-config (cmn/curact app-atom [:config :snapscan])
        totals-atom (sell/totals app-atom)
        {:keys [owing]} (rum/react totals-atom)
        pmt (cmn/curact app-atom [:payments :snapscan])]
    (screen-wrapper app-atom :payment
      [[:div.u-margin-top-24
        [:div {:class (u/bem :section [:medium])}
         [:div.card.payment-card
          [:div.payment-card__logo-wrapper.u-text-center
           [:img.payment-card__logo
            {:src "https://res.cloudinary.com/yuppiechef/image/upload/v1503397903/pos-ui/snapscan-logo.svg"
             :alt "SnapScan"}]]
          [:div.payment-card__graphic-wrapper
           [:img {:src (snapscancode (select-keys pmt [:amount :reference]) (:endpoint snapscan-config))}]]
          [:p.paragraph.paragraph--flush.u-text-14.u-text-uppercase.u-text-center
           "Scan QR Code to Pay"]]]
        [:div.section.u-text-right
         (buttons/button "Manual Capture"
           #(snapscan-manual-modal app-atom)
           {:secondary true :icon-left :pencil})]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-fn #(grid/dispatch-action app-atom :payment/capture (assoc pmt :pmttype "Snapscan"))
         :right-text "Confirm"}))))

(defmethod flow/render-screen :payment/snapscan [app-atom screen]
  (snapscan-screen app-atom))

(rum/defc ebucks-screen < rum/reactive
  [app-atom]
  (let [ebucks-atom (rum/cursor-in app-atom [:payments :ebucks])
        totals-atom (sell/totals app-atom)
        {:keys [owing]} (rum/react totals-atom)]
    (screen-wrapper app-atom :payment
      [[:div
        [:form.u-margin-top-24
         [:div {:class (u/bem :section [:medium])}

          (forms/input "Ebucks Amount Paid Using Card:" ""
            (rum/cursor ebucks-atom :amount)
            {:type :currency
             :hint (str "Owing: R" (u/currency-format owing))})
          (forms/input "Last 4 Digits of Card:" ""
            (rum/cursor ebucks-atom :reference)
            {:type :text-number :max-length 4})]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-fn #(capture-payment app-atom "eBucks" @ebucks-atom)}))))

(defmethod flow/render-screen :payment/ebucks [app-atom screen]
  (ebucks-screen app-atom))

(rum/defcs staff-screen < rum/reactive (rum/local {} ::state)
  [s app-atom]
  (let [local-atom (::state s)
        staff-atom (rum/cursor-in app-atom [:payments :staff])]
    (screen-wrapper app-atom :payment
      [[:div
        [:form.u-margin-top-24
         [:div {:class (u/bem :section [:medium])}
          (forms/input "Staff Email:" "" (rum/cursor staff-atom :reference)
            {:disabled true :hint "Change customer to change this"})
          (forms/input "Staff Password:" ""
            (rum/cursor local-atom :pwd)
            {:type :password})]]]]
      []
      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-text "Confirm"
         :right-fn #(capture-payment app-atom "Staff" (merge @staff-atom @local-atom))}))))

(defmethod flow/render-screen :payment/staff [app-atom screen]
  (staff-screen app-atom))

(defmethod flow/barcode-dispatch-screen :payment/staff [_ app-atom scan]
  (swap! app-atom assoc-in [:payments :staff :pwd] scan)
  (capture-payment app-atom "Staff" (get-in @app-atom [:payments :staff])))

(rum/defc confirm < rum/reactive
  [app-atom]
  (let [totals-atom (sell/totals app-atom)
        confirm-atom (rum/cursor-in app-atom [:payments :confirm])
        confirm (rum/react confirm-atom)
        {:keys [style confirm-msg] :as typedef} (get type-lookup (:pmttype confirm))
        {:keys [owing change]} (rum/react totals-atom)]
    (screen-wrapper app-atom :payment
      [[:div.u-margin-top-24
        (case style
          :card
          [:div {:class (u/bem :section [:medium])}
           [:div.card.payment-card
            [:div.payment-card__logo-wrapper.u-text-center
             [:img.payment-card__logo
              {:src (:logo typedef) :alt (:name typedef)}]]
            [:div.payment-card__graphic-wrapper
             [:i.u-block-center {:class (u/bem :icon [:block :enormous :success :3-3])}]]]]

          [:form.u-margin-top-24
           [:div {:class (u/bem :section [:medium])}
            [:h2.u-text-center {:class (u/bem :heading [:standard])} confirm-msg]
            [:i.u-block-center {:class (u/bem :icon [:block :enormous :success :3-3])}]

            (case (:pmttype confirm)
              "Cash"
              (when change
                [:div.u-margin-top-24
                 (feedback/action-feedback :info "Change Required: " (str "R" (u/currency-format change))
                   (rum/cursor confirm-atom :acknowledge) {})])
              "")]])]]
      []
      (grid/pos-call-to-action
        {:left-text (if (pos? owing) (str "Owing: R" (u/currency-format owing)) "Paid")
         :left-plain true
         :right-fn
         (if
           (and
             (= "Cash" (:pmttype confirm))
             (not (:acknowledge confirm))
             (not (nil? change))
             (pos? change))
           nil #(t/transition-next app-atom :payment))
         :right-text "Payments"}))))

(defmethod flow/render-screen :payment/confirm [app-atom screen]
  (confirm app-atom))

(defmethod grid/action :payment/capture [app-atom config _]
  (let [pmttype (:pmttype config)

        state @app-atom
        {:keys [owing total]} (sell/calc-totals-app-state state)
        {:keys [amount percentage reference pwd scan]} config

        ;; The input from the UI is always in Rands, this is the closest we
        ;; can get it to cents without doing all sorts of handstands at each
        ;; UI point.
        amount (when amount (* 100 (u/parseFloat amount)))]

    (toaster/open-toast app-atom :processing "Processing payment" {})
    (comms/send :pos/payment
      (sell/sell-comms app-atom
        (u/maybe assoc
          {:amount amount
           :reference reference
           :pwd (when (u/presence pwd) (hash/md5 pwd))
           :change
           (when (and (= pmttype "Cash") (> amount owing))
             (- amount owing))
           :pmttype pmttype
           :scan scan}
          :id (when (u/presence (:id config)) (:id config))
          :card-number (when (u/presence (:card-number config)) (:card-number config)))))))

(defmethod grid/action :payment/start-card-payment [app-atom config _]
  (let [{:keys [amount pmttype emulate]} (:payment config)
        amount-cents (when amount (* 100 (u/parseFloat amount)))
        {:keys [owing]} (sell/calc-totals-app-state @app-atom)]
    (cond
      ;; The payment app handover happens on this click — require a live provider now, not the
      ;; render-time snapshot. Covers the capability bridge dropping (e.g. after device sleep)
      ;; AND the never-present case, where a nil render-time pmttype would otherwise fall through
      ;; to :else and send a pending payment that hangs at "Provider not found".
      (and (not emulate) (nil? (provider)))
      (toaster/open-toast app-atom :error
        "Payment app unavailable — please capture a manual payment." {:closetime 4000})

      (and
        (= "Payflex" pmttype)
        amount-cents
        (not (<= 1000 (Math/round amount-cents) 5000000)))
      (toaster/open-toast app-atom :error
        "Payflex payments must be between R10 and R50 000." {:closetime 4000})

      (and
        (= "Payflex" pmttype)
        amount-cents
        (> (Math/round amount-cents) (Math/round owing)))
      (toaster/open-toast app-atom :error
        (str "Payflex payment cannot be more than the R" (u/currency-format owing) " owing.")
        {:closetime 4000})

      :else
      (do
        (toaster/open-toast app-atom :processing "Starting Payment" {:closetime 20000})
        (comms/send :pos/pending-payment
          (sell/sell-comms app-atom
            (assoc (:payment config) :amount amount-cents)))))))

;; ================== Thumbzup Manual Modal ===========================
(rum/defc thumbzup-manual-modal-body < rum/reactive [app-atom config-atom]
  (let [config (rum/react config-atom)]
    [:div.section
     (headings/heading "Thumbzup"
       {:type :h2
        :size :small
        :subtext "Manual Verification"})
     (when (:error-msg config)
       (feedback/feedback :warning (:error-msg config)))
     (forms/input "Amount Paid Using Card:" ""
       (rum/cursor config-atom :amount)
       {:type :currency
        :hint (str "Owing: R" (u/currency-format (:amount config)))})
     (forms/input "Last 4 Digits of Card:" ""
       (rum/cursor config-atom :card-number)
       {:type :text-number :max-length 4})]))

(rum/defc thumbzup-manual-modal-footer [app-atom]
  (grid/button-row
    {:single-text "Verify Payment"
     :single-style :primary
     :single-fn
     #(grid/dispatch-modal-action app-atom :payment/capture)}))

(defmethod grid/modal-config :payment/thumbzup-manual-modal [_ _ _]
  {:body thumbzup-manual-modal-body
   :footer thumbzup-manual-modal-footer})

(defn thumbzup-manual-modal [app-atom payment message]
  (toaster/close-toast app-atom)
  (grid/open-modal app-atom :payment/thumbzup-manual-modal
    (u/maybe assoc payment
      :pmttype "ThumbzupManual"
      :amount (double (/ (:amount payment) 100))
      :error-msg (when (u/presence message) message))))

;; ================== Thumbzup Manual Modal ===========================

(defmethod comms/receive :pos/payment-pending [app-atom {:keys [payment]}]
  ;; TODO: What's the point of a 60 second timeout and no-op close function?!!?
  (toaster/close-toast app-atom)

  (toaster/open-toast app-atom :processing "Launching Payment" {:closetime 60000})
  (swap! app-atom
    (fn [s]
      (->
        s
        (assoc-in [:log] {:msg "Launching Payment" :payment payment})
        (update-in [:sell :cart :payments] conj payment))))

  (t/transition-prev app-atom :payment)

  (let [state @app-atom
        fail-fn
        (fn [message]
          (comms/send :pos/void-payment
            (sell/sell-comms (atom state) {:id (:id payment) :tag :backoff}))
          (toaster/open-toast app-atom :error (str "Error: " message) {})
          (t/transition-prev app-atom :payment))]

    (payment/handle-call app-atom
      (:pmttype payment)
      (get-in state [:payments :card :launchtype] :sale)
      {:amount (:amount payment)
       :reference (:id payment)
       :payment-uuid (:paymentuuid payment)
       :receipt false
       :emulate (get-in state [:payments :card :emulate])}
      (fn [pmt]
        (comms/send :pos/payment
          (sell/sell-comms app-atom
            {:id (:id payment)
             :amount (float (:amount pmt))
             :reference (:reference pmt)
             :pmttype (:pmttype payment)})))
      fail-fn fail-fn)))

(defmethod comms/receive :pos/confirm-payment [app-atom e]
  (let [pmt
        (->
          e
          (set/rename-keys {:pmt_id :id})
          (select-keys [:id :amount :reference :pmttype :status :change]))]

    (toaster/open-toast app-atom :success "Payment captured" {})
    (t/transition-next app-atom :payment/confirm)
    (swap! app-atom
      (fn [state]
        (->
          state
          (assoc :payments {:confirm pmt})
          (update-in [:sell :cart :payments] (fn [l] (remove #(= (:id pmt) (:id %)) l)))
          (update-in [:sell :cart :payments] conj pmt))))))

(defmethod grid/action :payment/check-payment-status [app-atom config _]
  (let [payment (:payment config)]
    (cond
      ;; Refunded/voided payment: the original SALE tx stays APPROVED in Ecentric, so a status check
      ;; would TX_SEARCH the sale uuid, find it APPROVED, and re-confirm the payment. The refund
      ;; already voided it locally — block the re-check.
      (= "Voided" (:status payment))
      (toaster/open-toast app-atom :error "Payment already voided. Cannot check status." {})

      (ecentric-pmttypes (:pmttype payment))
      (do
        (toaster/open-toast app-atom :processing "Checking Payment Status" {:closetime 60000 :close-fn #()})
        (payment/handle-call app-atom
          (:pmttype payment)
          :check-status
          {:amount (:amount payment)
           :reference (:id payment)
           :uuid (:reference payment)
         ;; paymentuuid pre-confirm; reference post-confirm (both = the uuid sent as transactionUuid).
           :payment-uuid (or (:paymentuuid payment) (:reference payment))
           :emulate (get-in @app-atom [:payments :card :emulate])}
          (fn [pmt]
            (comms/send :pos/payment
              (sell/sell-comms app-atom
                {:id (:id payment)
                 :amount (float (:amount pmt))
                 :reference (:reference pmt)
                 :pmttype (:pmttype payment)})))
          ;; Void Payment on tx failure (not found, declined)
          (fn [message]
            (comms/send :pos/void-payment
              (sell/sell-comms app-atom {:id (:id payment) :tag :backoff}))
            (toaster/open-toast app-atom :error (str "Error: " message) {}))

          (fn [message]
            (if (= "Payflex" (:pmttype payment))
              (toaster/open-toast app-atom :error (str "Error: " message) {})
              (thumbzup-manual-modal app-atom payment message)))))

      :else
      (comms/send :pos/void-payment
        (sell/sell-comms app-atom
          {:id (:id payment)})))))

(defmethod grid/action :payment/remove-payment [app-atom config _]
  (let [payment (:payment config)]
    (cond
      (= "Payflex" (:pmttype payment))
      (toaster/open-toast app-atom :error "Successful Payflex payments cannot be voided. The order must be closed and the customer refunded through CS." {})

      :else
      (do
        (toaster/open-toast app-atom :processing "Voiding Payment" {:close-time 60000 :close-fn #()})
        ;; Only reverse at the terminal when there IS a captured transaction to reverse.
        ;; A Pending payment never completed, so :reference is blank — refunding it sends an
        ;; empty originalTransactionUuid, Ecentric answers TRANSACTION_NOT_FOUND, and the
        ;; payment would be left stranded in Pending. Void those locally instead.
        (if (and
              (= "Thumbzup" (:pmttype payment))
              (u/presence (:reference payment)))
          (payment/handle-call app-atom (:pmttype payment)
            :refund
            {:amount (:amount payment)
             :reference (:id payment)
             :uuid (:reference payment)}
            (fn [p]
              (comms/send :pos/void-payment
                (sell/sell-comms app-atom
                  {:id (:id payment)})))
            (fn [message]
              (toaster/open-toast app-atom :error (str "Fail: " message) {}))
            (fn [message]
              (toaster/open-toast app-atom :error (str "Error: " message) {})))

          (comms/send :pos/void-payment
            (sell/sell-comms app-atom
              {:id (:id payment)})))))))

(defmethod comms/receive :pos/remove-payment [app-atom e]
  (when-not (= (:tag e) :backoff)
    (toaster/open-toast app-atom :success "Payment Voided" {}))

  (swap! app-atom update-in [:sell :cart :payments]
    (fn [pmtlist]
      (->>
        pmtlist
        (map
          (fn [p]
            (if (= (:id e) (:id p))
              ;; Terminal payments stay listed as Voided — the void is an auditable event the
              ;; operator needs to see. Everything else disappears from the cart.
              (if (ecentric-pmttypes (:pmttype p))
                (assoc p :status "Voided")
                nil)
              p)))
        (remove nil?)))))

